
import type { AccountType } from "@/modules/safecheck/auth";
import { isSuperAdminUser } from "@/modules/safecheck/auth";

const superAdminSystemSection: NavSection = {
  id: "system",
  titleKey: "systemSection",
  items: [
    {
      id: "backup",
      iconName: "backup",
      children: [
        { id: "createBackup", href: "/dashboard/backup/create" },
        { id: "restoreBackup", href: "/dashboard/backup/restore" },
      ],
    },
  ],
};

/**
 * Menus alignés sur le circuit SafeCheck Assurance (16/10/2026) :
 * Admin saisit les factures (par assureur) → Audit code et valide → Sinistre contrôle et envoie la note de paiement
 * → Assurance consulte, saisit les demandes de validation et gère ses gestionnaires.
 * Retirés : forfaits, conventions, facturation entreprise, admissions, litiges, rapports d'audit libres, messagerie.
 */
export const mainSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "main",
      items: [{ id: "dashboard", iconName: "dashboard", href: "/dashboard/admin" }],
    },
    {
      id: "structure",
      titleKey: "structure",
      items: [
        {
          id: "invoices",
          iconName: "invoices",
          children: [
            { id: "submitInvoice", href: "/dashboard/invoices/submit" },
            { id: "allInvoices", href: "/dashboard/invoices" },
            { id: "approvedInvoices", href: "/dashboard/invoices/approved" },
            { id: "rejectedInvoices", href: "/dashboard/invoices/rejected" },
          ],
        },
        { id: "requests", iconName: "requests", href: "/dashboard/requests" },
      ],
    },
    {
      id: "gestion",
      titleKey: "management",
      items: [
        // Gestion : assureurs (grilles depuis leur fiche), packages, prestataires, entreprises, référentiels.
        { id: "insurers", iconName: "insurers", href: "/dashboard/insurers" },
        { id: "claimsPackages", iconName: "packages", href: "/dashboard/claims/packages" },
        {
          id: "providers",
          iconName: "hospital",
          children: [
            { id: "allProviders", href: "/dashboard/providers" },
            { id: "addProvider", href: "/dashboard/providers/create" },
          ],
        },
        // Entreprises du catalogue (avec leur portefeuille d'agents), gérées comme les prestataires.
        {
          id: "companies",
          iconName: "companies",
          children: [
            { id: "allCompanies", href: "/dashboard/companies" },
            { id: "addCompany", href: "/dashboard/companies/create" },
          ],
        },
        {
          id: "codeReferentials",
          iconName: "referentials",
          children: [
            { id: "actCodes", href: "/dashboard/referentials/acts" },
            { id: "affectionCodes", href: "/dashboard/referentials/affections" },
            { id: "hospitalActs", href: "/dashboard/acts/by-hospital" },
          ],
        },
      ],
    },
    // Reporting tous assureurs (cahier des charges, lot 4), menu à part.
    {
      id: "reporting-section",
      titleKey: "reportingSection",
      items: [{ id: "reporting", iconName: "reporting", href: "/dashboard/reporting" }],
    },
    {
      id: "configuration",
      titleKey: "configuration",
      items: [
        {
          id: "users",
          iconName: "accounts",
          children: [
            { id: "allAccounts", href: "/dashboard/users" },
            { id: "addUser", href: "/dashboard/users/create" },
          ],
        },
        { id: "supportRequests", iconName: "requests", href: "/dashboard/support-requests" },
        { id: "deadlines", iconName: "deadlines", href: "/dashboard/deadlines" },
        { id: "exchangeRates", iconName: "exchange", href: "/dashboard/exchange-rates" },
        {
          id: "settings",
          iconName: "settings",
          children: [{ id: "userProfile", href: "/dashboard/settings/user" }],
        },
      ],
    },
  ],
};

/** Navigation compte Audit (médecins SafeCheck) */
export const auditorSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "auditor-main",
      items: [{ id: "auditorDashboard", iconName: "dashboard", href: "/dashboard/auditor" }],
    },
    {
      id: "audit",
      titleKey: "auditSection",
      items: [
        {
          id: "invoices",
          iconName: "invoices",
          children: [
            { id: "allInvoices", href: "/dashboard/invoices" },
            { id: "approvedInvoices", href: "/dashboard/invoices/approved" },
            { id: "rejectedInvoices", href: "/dashboard/invoices/rejected" },
          ],
        },
        { id: "requestsToValidate", iconName: "requests", href: "/dashboard/requests" },
        {
          id: "statistics",
          iconName: "statistics",
          children: [{ id: "globalStatistics", href: "/dashboard/statistics" }],
        },
      ],
    },
    {
      id: "consultation",
      titleKey: "consultation",
      items: [
        {
          id: "codeReferentials",
          iconName: "referentials",
          children: [
            { id: "actCodes", href: "/dashboard/referentials/acts" },
            { id: "affectionCodes", href: "/dashboard/referentials/affections" },
            { id: "hospitalActs", href: "/dashboard/acts/by-hospital" },
          ],
        },
      ],
    },
  ],
};

/** Navigation compte Assurance (clé historique « enterprise ») */
export const enterpriseSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "enterprise-main",
      items: [
        { id: "enterpriseDashboard", iconName: "dashboard", href: "/dashboard/enterprise" },
        { id: "claimsReport", iconName: "report", href: "/dashboard/report" },
        { id: "requests", iconName: "requests", href: "/dashboard/requests" },
        {
          id: "invoices",
          iconName: "invoices",
          children: [
            { id: "allInvoices", href: "/dashboard/invoices" },
            { id: "approvedInvoicesToPay", href: "/dashboard/invoices/approved" },
            { id: "decomptes", href: "/dashboard/decomptes" },
          ],
        },
      ],
    },
    {
      id: "portfolio",
      titleKey: "management",
      // Ordre demandé : gestion des assurés, réseau hospitalier de l'assureur, couverture.
      items: [
        {
          id: "employeeManagement",
          iconName: "employees",
          children: [
            { id: "employeeList", href: "/dashboard/employees" },
            { id: "beneficiaryList", href: "/dashboard/beneficiaries" },
          ],
        },
        { id: "insurerNetwork", iconName: "network", href: "/dashboard/network" },
        { id: "coverage", iconName: "coverage", href: "/dashboard/coverage" },
      ],
    },
    {
      id: "configuration",
      titleKey: "configuration",
      items: [
        {
          id: "settings",
          iconName: "settings",
          children: [{ id: "userProfile", href: "/dashboard/settings/user" }],
        },
      ],
    },
  ],
};

/** Navigation compte Sinistre (clé historique « hospital ») */
export const hospitalSidebarConfig: MainSidebarConfig = {
  sections: [
    {
      id: "hospital-main",
      items: [{ id: "hospitalDashboard", iconName: "dashboard", href: "/dashboard/hospital" }],
    },
    {
      id: "operations",
      titleKey: "operations",
      items: [
        { id: "claimsControl", iconName: "control", href: "/dashboard/claims/control" },
        { id: "viewInvoices", iconName: "invoices", href: "/dashboard/invoices" },
        { id: "claimsSettlements", iconName: "settlements", href: "/dashboard/claims/settlements" },
      ],
    },
    {
      id: "referentials",
      titleKey: "management",
      items: [
        { id: "claimsPortfolio", iconName: "portfolio", href: "/dashboard/claims/portfolio" },
        { id: "claimsPackages", iconName: "packages", href: "/dashboard/claims/packages" },
        { id: "claimsGrids", iconName: "grids", href: "/dashboard/claims/grids" },
        {
          id: "codeReferentials",
          iconName: "referentials",
          children: [
            { id: "actCodes", href: "/dashboard/referentials/acts" },
            { id: "affectionCodes", href: "/dashboard/referentials/affections" },
            { id: "medicalActs", href: "/dashboard/acts/by-hospital" },
            { id: "categoryValidation", href: "/dashboard/acts/category-validation" },
          ],
        },
      ],
    },
    {
      id: "configuration",
      titleKey: "configuration",
      items: [
        {
          id: "settings",
          iconName: "settings",
          children: [{ id: "userProfile", href: "/dashboard/settings/user" }],
        },
      ],
    },
  ],
};

export function getSidebarConfig(accountType: AccountType | null): MainSidebarConfig {
  if (accountType === "enterprise") return enterpriseSidebarConfig;
  if (accountType === "hospital") return hospitalSidebarConfig;
  if (accountType === "auditor") return auditorSidebarConfig;
  if (accountType === "admin" && isSuperAdminUser()) {
    return {
      sections: [...mainSidebarConfig.sections, superAdminSystemSection],
    };
  }
  return mainSidebarConfig;
}
